import React, { useState, useEffect, useMemo } from "react";
import {
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Bar,
  ComposedChart,
} from "recharts";
import {
  Typography,
  Box,
  CircularProgress,
  useTheme,
  useMediaQuery,
  alpha,
  Card,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tooltip as MuiTooltip,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InfoIcon from "@mui/icons-material/Info";
import useParsedTSVCache from "../hooks/useParsedTSVCache";

const MONEY_SUPPLY_BASE_VALUE = 29056443; // Configurable base value

const METRIC_CONFIG = [
  { key: "money_created", label: "Money Created", dataKey: "money_created" },
  { key: "money_deleted", label: "Money Deleted", dataKey: "money_deleted" },
  {
    key: "money_transferred",
    label: "Money Transferred",
    dataKey: "money_transferred",
  },
  { key: "net_change", label: "Net Change", dataKey: "net_change" },
];

const MoneySupplyData = ({
  title = "Money Supply Metrics",
  subtitle = "",
  initialTimeFilter = null,
  onFilterChange = () => {},
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"), { noSsr: true });

  const [visibleMetrics, setVisibleMetrics] = useState({
    money_created: true,
    money_deleted: true,
    money_transferred: true,
    net_change: true,
  });
  const [error, setError] = useState(null);

  // Transform function to clean currency values
  const transformRow = (row) => {
    // Helper to parse currency strings like "$1,234.56" to numbers
    const parseCurrency = (value) => {
      if (typeof value === "number") return value;
      if (!value) return 0;
      // Remove $, commas, and convert to number
      const cleaned = value.toString().replace(/[$,]/g, "");
      const num = parseFloat(cleaned);
      return isNaN(num) ? 0 : num;
    };

    return {
      ...row,
      money_created: parseCurrency(row.money_created),
      money_deleted: parseCurrency(row.money_deleted),
      money_transferred: parseCurrency(row.money_transferred),
      net_change: parseCurrency(row.net_change),
      total_transactions:
        typeof row.total_transactions === "number"
          ? row.total_transactions
          : parseInt(row.total_transactions) || 0,
    };
  };

  // Use parsed TSV cache - data is automatically parsed
  const {
    data: parsedData,
    loading: isLoading,
    error: fetchError,
  } = useParsedTSVCache(
    "money_supply_cache_v2", // Updated cache key for new transform
    "/markets-data/money-supply-latest.tsv",
    {
      header: true,
      delimiter: "\t",
      skipEmptyLines: true,
      dynamicTyping: false, // Disable auto-typing so we can parse currency ourselves
    },
    3600000, // 1 hour TTL
    transformRow, // Post-parse transformation
  );

  // Set error from fetch if any
  useEffect(() => {
    if (fetchError) {
      setError(`Error fetching data: ${fetchError}`);
    }
  }, [fetchError]);

  // Sort data chronologically
  const sortedData = useMemo(() => {
    if (!parsedData || parsedData.length === 0) return [];

    // Filter out rows with missing month and sort chronologically
    return [...parsedData]
      .filter((item) => item.month && item.month.toString().trim() !== "")
      .sort((a, b) => {
        const monthA = a.month || "";
        const monthB = b.month || "";
        return monthA.localeCompare(monthB);
      });
  }, [parsedData]);

  // Calculate estimated total cash
  const estimatedTotalCash = useMemo(() => {
    if (!sortedData || sortedData.length === 0) return MONEY_SUPPLY_BASE_VALUE;

    return sortedData.reduce(
      (total, item) => total + (item.net_change || 0),
      MONEY_SUPPLY_BASE_VALUE,
    );
  }, [sortedData]);

  const handleToggleMetric = (metric) => {
    // Prevent hiding all metrics
    const currentlyVisible =
      Object.values(visibleMetrics).filter(Boolean).length;
    if (currentlyVisible === 1 && visibleMetrics[metric]) {
      return; // Don't allow hiding the last visible metric
    }

    setVisibleMetrics((prev) => ({
      ...prev,
      [metric]: !prev[metric],
    }));
  };

  const getMetricColor = (metricKey) => {
    const colorMap = {
      money_created: theme.palette.success.main,
      money_deleted: theme.palette.error.main,
      money_transferred: theme.palette.info.main,
      net_change: theme.palette.secondary.main,
    };
    return colorMap[metricKey] || theme.palette.primary.main;
  };

  // Format month label for display (show as-is for months like "2025-01")
  const formatMonthLabel = (month) => {
    if (!month) return "";
    return month;
  };

  // Format large numbers compactly (e.g., 260k, 1.2M)
  const formatCompactNumber = (value) => {
    if (value >= 1000000) {
      const millions = value / 1000000;
      return millions < 10
        ? `${millions.toFixed(1)}M`
        : `${millions.toFixed(0)}M`;
    }
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands < 10
        ? `${thousands.toFixed(1)}k`
        : `${thousands.toFixed(0)}k`;
    }
    return value.toFixed(0);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          color: "error.main",
        }}
      >
        {error}
      </Box>
    );
  }

  // Custom tooltip for main chart
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Card sx={{ p: 1.5, boxShadow: 2, bgcolor: "background.paper" }}>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            {label}
          </Typography>
          {payload
            .filter(
              (entry) => entry.value !== null && entry.value !== undefined,
            )
            .map((entry, index) => (
              <Box key={index} sx={{ mb: 0.5 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: entry.color,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Box
                    sx={{
                      width: 12,
                      height: 12,
                      bgcolor: entry.color,
                      mr: 1,
                      borderRadius: 0.5,
                    }}
                  />
                  {entry.name}:{" "}
                  {typeof entry.value === "number"
                    ? entry.value.toLocaleString()
                    : entry.value}
                </Typography>
              </Box>
            ))}
        </Card>
      );
    }
    return null;
  };

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <AccountBalanceIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
        <Typography variant="h5">{title}</Typography>
      </Box>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {subtitle}
        </Typography>
      )}

      {/* Metric Visibility Controls */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Visible Metrics:
        </Typography>
        <FormGroup row>
          {METRIC_CONFIG.map((metric) => {
            const currentlyVisible =
              Object.values(visibleMetrics).filter(Boolean).length;
            const isDisabled =
              currentlyVisible === 1 && visibleMetrics[metric.key];

            return (
              <FormControlLabel
                key={metric.key}
                control={
                  <Checkbox
                    checked={visibleMetrics[metric.key]}
                    onChange={() => handleToggleMetric(metric.key)}
                    disabled={isDisabled}
                    sx={{
                      color: getMetricColor(metric.key),
                      "&.Mui-checked": {
                        color: getMetricColor(metric.key),
                      },
                    }}
                  />
                }
                label={metric.label}
              />
            );
          })}
        </FormGroup>
      </Box>

      {/* Estimated Total Cash Display */}
      {sortedData.length > 0 && (
        <Card
          sx={{ mb: 3, p: 2, bgcolor: alpha(theme.palette.success.main, 0.1) }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AccountBalanceIcon color="success" fontSize="large" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography
                variant="h4"
                color="success.main"
                sx={{ fontWeight: "bold" }}
              >
                ${estimatedTotalCash.toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Estimated Total Cash in Economy
              </Typography>
            </Box>
            <MuiTooltip title="Calculated by summing net_change values." arrow>
              <InfoIcon
                fontSize="small"
                color="action"
                sx={{ cursor: "help" }}
              />
            </MuiTooltip>
          </Box>
        </Card>
      )}

      {sortedData.length > 0 ? (
        <>
          {/* Main Chart - Money Flow Metrics */}
          <ResponsiveContainer width="100%" height={isMobile ? 350 : 400}>
            <ComposedChart
              data={sortedData}
              margin={{
                top: 20,
                right: isMobile ? 5 : 30,
                left: isMobile ? 5 : 20,
                bottom: 10,
              }}
            >
              <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.6} />
              <XAxis
                dataKey="month"
                tick={{ fill: theme.palette.text.primary, fontSize: 11 }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={formatMonthLabel}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                yAxisId="left"
                orientation="left"
                tick={{
                  fill: theme.palette.text.primary,
                  fontSize: isMobile ? 10 : 12,
                }}
                tickLine={{ stroke: theme.palette.divider }}
                axisLine={{ stroke: theme.palette.divider }}
                tickFormatter={formatCompactNumber}
                label={{
                  value: "Amount ($)",
                  angle: -90,
                  position: "insideLeft",
                  style: {
                    textAnchor: "middle",
                    fill: theme.palette.text.secondary,
                    fontSize: isMobile ? 10 : 12,
                  },
                }}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{
                  paddingTop: isMobile ? "10px" : "20px",
                  fontSize: isMobile ? "12px" : "14px",
                }}
                iconSize={isMobile ? 12 : 14}
                height={isMobile ? 40 : 36}
              />
              {METRIC_CONFIG.map(
                (metric) =>
                  visibleMetrics[metric.key] && (
                    <Line
                      key={metric.key}
                      type="monotone"
                      dataKey={metric.dataKey}
                      name={metric.label}
                      yAxisId="left"
                      stroke={getMetricColor(metric.key)}
                      strokeWidth={2}
                      dot={{ r: 3, fill: getMetricColor(metric.key) }}
                      activeDot={{ r: 5 }}
                      connectNulls={true}
                    />
                  ),
              )}
            </ComposedChart>
          </ResponsiveContainer>

          {/* Volume Mini-Chart - Total Transactions */}
          <Box sx={{ mt: 3 }}>
            <ResponsiveContainer width="100%" height={isMobile ? 80 : 120}>
              <ComposedChart
                data={sortedData}
                margin={{
                  top: 5,
                  right: isMobile ? 5 : 30,
                  left: isMobile ? 5 : 20,
                  bottom: 5,
                }}
              >
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.3} />
                <XAxis
                  dataKey="month"
                  tickFormatter={formatMonthLabel}
                  tick={{
                    fill: theme.palette.text.primary,
                    fontSize: 10,
                  }}
                  tickLine={{ stroke: theme.palette.divider }}
                  axisLine={{ stroke: theme.palette.divider }}
                  angle={-45}
                  textAnchor="end"
                  height={40}
                />
                <YAxis
                  orientation="left"
                  tickFormatter={formatCompactNumber}
                  tick={{
                    fill: theme.palette.text.primary,
                    fontSize: 10,
                  }}
                  tickLine={{ stroke: theme.palette.divider }}
                  axisLine={{ stroke: theme.palette.divider }}
                />
                <Tooltip
                  cursor={{ fill: "rgba(0,0,0,0.1)" }}
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      const dataPoint = payload[0]?.payload;
                      return (
                        <Card
                          sx={{
                            p: 1,
                            boxShadow: 2,
                            bgcolor: "background.paper",
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{ display: "block", mb: 0.5 }}
                          >
                            {dataPoint?.month || label}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ color: theme.palette.warning.main }}
                          >
                            <strong>Transactions:</strong>{" "}
                            {dataPoint?.total_transactions?.toLocaleString() ||
                              0}
                          </Typography>
                        </Card>
                      );
                    }
                    return null;
                  }}
                />
                <Bar
                  dataKey="total_transactions"
                  name="Total Transactions"
                  fill={alpha(theme.palette.warning.main, 0.7)}
                  radius={[2, 2, 0, 0]}
                />
                <Legend
                  wrapperStyle={{
                    paddingTop: "10px",
                  }}
                  iconSize={isMobile ? 10 : 14}
                />
              </ComposedChart>
            </ResponsiveContainer>
          </Box>
        </>
      ) : (
        <Box
          sx={{
            height: 400,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: alpha(theme.palette.primary.main, 0.03),
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle1" color="text.secondary">
            No money supply data available
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default MoneySupplyData;
